/**
 * Admin Analytics API client — feeds the WEB admin analytics dashboard at
 * `/admin/analytics` (F9, SS backlog parallel sprint 2026-07-06, squad WEB-DASH).
 *
 * READ-ONLY. Reuses existing backend aggregates — NO new backend endpoints.
 * Backend response convention is `{ success, data }`; these helpers unwrap to
 * the raw payload. All routes are relative to the axios baseURL `/api/v1`.
 *
 * Endpoint sources (verified against backend route files 2026-07-06):
 *   - role-kpis  → mounted at BOTH /analytics AND /role-kpis. Standard KpiPayload
 *                  shape `{ value, breakdown?, meta? }`. Scope via `?scope=MY|TEAM|DEPT|ORG`,
 *                  resolved from the AUTHENTICATED user's reportsToId/departmentId
 *                  — NOT an arbitrary rep/team id (see gap notes in the page).
 *   - scorecard  → /analytics/scorecard/{me,team,org}. `me?userId=<X>` lets a
 *                  super-role inspect a SPECIFIC user (Employee scope).
 *   - triage     → /leads/triage-dashboard. Avika hot/warm/cold lead feed with
 *                  `?tier=HOT|WARM|COLD|BLANK`, date + stage filters, pagination.
 *   - analytics  → /analytics/{stats,pipeline,revenue}. Org-wide rollups.
 *   - teams      → /team/owner-filter-candidates (rep dropdown, User ids),
 *                  /teams (team dropdown).
 */
import { api } from '@/utils/api';

// ─── Shared shapes ───────────────────────────────────────────────────────────

/** Canonical KpiPayload returned by every role-kpis endpoint. */
export interface KpiPayload {
  value: number;
  delta?: number | null;
  trend?: Array<{ label: string; value: number }> | null;
  breakdown?: Array<{ label: string; value: number }>;
  meta?: Record<string, unknown> | null;
}

export type Scope = 'MY' | 'TEAM' | 'DEPT' | 'ORG';

/** Row from GET /leads/triage-dashboard (Avika-scored lead feed). */
export interface TriageRow {
  id: string;
  createdAt: string;
  enquiryDate: string;
  source: string | null;
  name: string | null;
  phone: string | null;
  email: string | null;
  location: string | null;
  requirement: string | null;
  applicationType: string | null;
  businessSegment: string | null;
  verifiedStatus: 'verified' | 'unverified';
  verifiedBy: 'avika' | 'chatkart' | 'manual' | null;
  verifiedByName: string | null;
  assignedToName: string | null;
  teamName: string | null;
  coordinatorName: string | null;
  score: number | null;
  tier: string | null; // 'HOT' | 'WARM' | 'COLD' | null
  scoringModel: string | null;
  capacity: string | null;
  timeline: string | null;
  state: string | null;
  waLineLabel: string | null;
  isB2bPriority: boolean;
  isOutOfBudget: boolean;
}

export interface TriageListResponse {
  data: TriageRow[];
  total: number;
  tab: string;
  limit: number;
  offset: number;
}

/** Wire contract for GET /analytics/scorecard/me. */
export interface ScorecardDetail {
  userId: string;
  name: string;
  roleKey: string;
  score: number | null;
  standing: { name: string; color: string } | null;
  periodStart: string;
  periodEnd: string;
  frequency: string;
  deltaVsPrev: number | null;
  criteria: Array<{ name: string; weight: number; achievementPct: number | null; band: string }>;
  tiles: Array<{ label: string; value: string | number; unit?: string }>;
}

export interface TeamRow {
  userId: string;
  name: string;
  roleKey: string;
  score: number | null;
  standing: { name: string; color: string } | null;
  metrics: Record<string, number | null>;
}

/** Org-wide stats from GET /analytics/stats. */
export interface OrgStats {
  contacts: number;
  accounts: number;
  deals: { total: number; won: number; lost: number; open: number };
  invoices: { total: number; draft: number; paid: number; overdue: number };
  revenue: { totalInvoiced: number; totalCollected: number };
  projects?: { active: number };
  serviceTickets?: { open: number };
  employees?: { active: number };
}

export interface PipelineStage {
  stageName?: string;
  stage?: string;
  totalValue?: number;
  value?: number;
  count?: number;
}

/** Rep option (User id) for the selector. */
export interface RepOption {
  id: string;
  name: string;
}

export interface TeamOption {
  id: string;
  name: string;
}

// ─── Selector-scope query builder ────────────────────────────────────────────

export interface DateWindow {
  /** ISO yyyy-mm-dd inclusive, or null for open-ended. */
  from: string | null;
  to: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function scopeParams(scope: Scope): Record<string, string> {
  return { scope };
}

/** Fetch a single role-kpi payload. `path` e.g. 'sales/rep-leaderboard'. */
export async function fetchKpi(path: string, scope: Scope = 'ORG', extra?: Record<string, string>): Promise<KpiPayload> {
  const res = await api.get(`/analytics/${path}`, { params: { ...scopeParams(scope), ...(extra ?? {}) } });
  // SBK-WEB-R6: guard against an envelope whose `.data` is missing/malformed.
  // Returning a well-formed empty KpiPayload (value:0, breakdown:[]) means the
  // page's `ava?.repLeaderboard.breakdown` / `.value` reads never TypeError into
  // a white page — they degrade to empty charts instead.
  const payload = (res.data?.data ?? {}) as Partial<KpiPayload>;
  return { value: payload.value ?? 0, breakdown: payload.breakdown ?? [], ...payload } as KpiPayload;
}

/**
 * Assignment-vs-Action bundle for the current scope.
 *
 * NOTE (gap): the role-kpi endpoints scope to the AUTHENTICATED user's tree
 * (MY = self, TEAM = self+direct-reports, ORG = everyone). They do NOT accept
 * an arbitrary rep/team id, so "inspect rep X" / "inspect team Y" as an admin
 * is only possible for the current user's own subtree. For per-rep / per-team
 * breakdowns we use the `breakdown[]` arrays (rep-leaderboard, quota-attainment)
 * which ARE per-rep. See page notes.
 */
export async function fetchAssignmentVsAction(scope: Scope): Promise<{
  repLeaderboard: KpiPayload;        // won ₹ this month per rep (target attainment, breakdown[])
  quotaAttainment: KpiPayload;       // collected revenue per rep MTD (breakdown[])
  activitiesToday: KpiPayload;       // total activities today (value)
  leadSourceMix: KpiPayload;         // lead count per source (breakdown[])
  stageConversion: KpiPayload;       // open deals per stage (breakdown[])
  overdueFollowups: KpiPayload;      // count (value)
}> {
  const [repLeaderboard, quotaAttainment, activitiesToday, leadSourceMix, stageConversion, overdueFollowups] =
    await Promise.all([
      fetchKpi('sales/rep-leaderboard', scope),
      fetchKpi('sales/quota-attainment', scope),
      fetchKpi('sales/team-activities-today', scope),
      fetchKpi('sales/lead-source-mix', scope),
      fetchKpi('sales/stage-conversion', scope),
      fetchKpi('sales/overdue-followups', scope),
    ]);
  return { repLeaderboard, quotaAttainment, activitiesToday, leadSourceMix, stageConversion, overdueFollowups };
}

/** Hot/warm/cold lead feed (Avika score tier). */
export async function fetchLeadFeed(opts: {
  tier?: 'any' | 'HOT' | 'WARM' | 'COLD' | 'BLANK';
  window?: DateWindow;
  tab?: 'unassigned' | 'assigned' | 'self_made';
  limit?: number;
  offset?: number;
  sort?: 'newest' | 'score_desc' | 'score_asc';
  search?: string;
}): Promise<TriageListResponse> {
  const params: Record<string, string | number> = {
    tab: opts.tab ?? 'assigned',
    tier: opts.tier ?? 'any',
    sort: opts.sort ?? 'score_desc',
    limit: opts.limit ?? 50,
    offset: opts.offset ?? 0,
  };
  if (opts.search) params.search = opts.search;
  if (opts.window?.from) params.dateFrom = opts.window.from;
  if (opts.window?.to) params.dateTo = opts.window.to;
  const res = await api.get('/leads/triage-dashboard', { params });
  const body = res.data;
  return { data: body.data ?? [], total: body.total ?? 0, tab: body.tab, limit: body.limit, offset: body.offset };
}

/** Scorecard for a specific user (Employee scope). Super-roles may pass any userId. */
export async function fetchScorecard(userId?: string): Promise<ScorecardDetail | null> {
  const res = await api.get('/analytics/scorecard/me', { params: userId ? { userId } : {} });
  return (res.data.data ?? null) as ScorecardDetail | null;
}

/** Team scorecard rows (self + direct reports of the caller). */
export async function fetchTeamScorecard(): Promise<TeamRow[]> {
  const res = await api.get('/analytics/scorecard/team');
  const data = res.data.data;
  return (data?.rows ?? []) as TeamRow[];
}

/** Org-wide stats + pipeline for KPI tiles + hot-lead history proxy. */
export async function fetchOrgStats(): Promise<OrgStats> {
  const res = await api.get('/analytics/stats');
  // SBK-WEB-R6: if the envelope shape differs, hand back a zeroed OrgStats so
  // the page's `orgStats.deals.total` / `.revenue.totalCollected` reads (which
  // run after a truthy null-check) don't TypeError.
  const d = (res.data?.data ?? {}) as Partial<OrgStats>;
  return {
    contacts: d.contacts ?? 0,
    accounts: d.accounts ?? 0,
    deals: d.deals ?? { total: 0, won: 0, lost: 0, open: 0 },
    invoices: d.invoices ?? { total: 0, draft: 0, paid: 0, overdue: 0 },
    revenue: d.revenue ?? { totalInvoiced: 0, totalCollected: 0 },
    ...d,
  } as OrgStats;
}

export async function fetchPipeline(): Promise<PipelineStage[]> {
  const res = await api.get('/analytics/pipeline');
  const raw = res.data.data;
  return (raw?.stages ?? raw ?? []) as PipelineStage[];
}

// ─── Selector option loaders ─────────────────────────────────────────────────

/** Reps (User ids) for the Employee selector. */
export async function fetchRepOptions(): Promise<RepOption[]> {
  try {
    const res = await api.get('/team/owner-filter-candidates');
    const data = res.data.data ?? [];
    // Candidate rows carry {id, firstName, lastName, name?}; normalize.
    return (Array.isArray(data) ? data : []).map((u: any) => ({
      id: u.id,
      name: u.name ?? (`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || u.id),
    }));
  } catch {
    return [];
  }
}

/** Teams for the Team selector. */
export async function fetchTeamOptions(): Promise<TeamOption[]> {
  try {
    const res = await api.get('/teams');
    const data = res.data.data ?? res.data ?? [];
    return (Array.isArray(data) ? data : []).map((t: any) => ({ id: t.id, name: t.name }));
  } catch {
    return [];
  }
}
